import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { emailValidation } from "models/validations";
import { Auth } from "aws-amplify";
import { useAppDispatch } from "store/hooks";
import { showError, hideError } from "store/reducers/errorReducer";
import { showSpinner, hideSpinner } from "store/reducers/spinnerReducer";
import { getApiErrorMessage } from "utils/getApiErrorMessage";

export const passwordRecoverySteps = {
  ENTER_EMAIL: "ENTER_EMAIL",
  ENTER_CODE: "ENTER_CODE",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
};

export const usePasswordRecoveryPage = () => {
  const navigate = useNavigate();
  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      username: "",
      code: "",
      newPassword: "",
      passwordRepeat: "",
    },
    mode: "onSubmit",
  });
  const [currentStep, setCurrentStep] = useState(
    passwordRecoverySteps.ENTER_EMAIL
  );

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const newPassword = useRef("");
  newPassword.current = watch("newPassword", "");

  const usernameRegisterProps = register("username", emailValidation);

  const dispatch = useAppDispatch();

  const onEmailSubmit = handleSubmit(async (newData) => {
    if (!newData) return;

    const { username } = newData;
    dispatch(showSpinner());
    dispatch(hideError());
    try {
      await Auth.forgotPassword(username);
      setCurrentStep(passwordRecoverySteps.ENTER_CODE);
    } catch (error) {
      dispatch(showError(getApiErrorMessage(error)));
    } finally {
      dispatch(hideSpinner());
    }
  });

  const onCodeAndPasswordSubmit = handleSubmit(async (newData) => {
    if (!newData) return;

    const { username, code, newPassword } = newData;
    dispatch(showSpinner());
    dispatch(hideError());
    try {
      await Auth.forgotPasswordSubmit(username, code, newPassword);
      setCurrentStep(passwordRecoverySteps.SUCCESS);
    } catch (error) {
      dispatch(showError(getApiErrorMessage(error)));
    } finally {
      dispatch(hideSpinner());
    }
  });

  const resendCode = () => {
    setCurrentStep(passwordRecoverySteps.CODE_REQUEST);
    onEmailSubmit();
  };

  return {
    errors,
    onEmailSubmit,
    onCodeAndPasswordSubmit,
    usernameRegisterProps,
    currentStep,
    resendCode,
    control,
    isPasswordVisible,
    setIsPasswordVisible,
    newPassword,
    navigate,
  };
};
